import { useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  ModalActions,
  TextArea,
  Toast,
  showToast,
} from '@evogard/ui';
import { CLAIM_CLOSURE_WARRANTY, claimClosureActionOf } from '@evogard/domain';
import { CLOSURE_ORIGIN_LABEL, closurePhaseLabel } from './closureLabels.js';
import { ClosureRefusal, useReopenClosure, type ReopenTarget } from './useClosures.js';

/** O número como a casa o dita: o sinistro pelo número, o acionamento com o P. */
export function closureNumberOf(c: Pick<ReopenTarget, 'source' | 'id'>): string {
  return c.source === 'periferico' ? `P-${c.id}` : `#${c.id}`;
}

/**
 * Reabrir: o motivo é obrigatório, e vira comentário. O destino não se
 * escolhe — é a fase de onde ele saiu, no mesmo departamento, com prazo novo
 * (decisão do dono, 13/10/2026).
 *
 * Acionar a garantia (22/10/2026) é o mesmo ato para o Concluído no Reparo:
 * ele vai a Retorno/Garantia, e o motivo é o "Motivo de Retorno" de lá.
 *
 * Abre da lista de Encerrados e da mesa do encerrado — a mesma janela nos dois.
 */
export function ReopenModal({
  closure,
  onClose,
}: {
  closure: ReopenTarget;
  onClose: () => void;
}) {
  const reopen = useReopenClosure();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const origin = CLOSURE_ORIGIN_LABEL[closure.origin];
  const number = closureNumberOf(closure);
  const warranty = claimClosureActionOf(closure.origin, closure.phase) === 'garantia';
  const back = warranty
    ? closurePhaseLabel(closure.origin, CLAIM_CLOSURE_WARRANTY.to)
    : closure.fromPhase
      ? closurePhaseLabel(closure.origin, closure.fromPhase)
      : null;

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${warranty ? 'Acionar a garantia do' : 'Reabrir'} ${number}`}
      descricao={
        back
          ? `Ele volta para ${back}, em ${origin}, com o prazo da fase recomeçando.`
          : `Ele volta para a fase de onde saiu, em ${origin}, com o prazo recomeçando.`
      }
      icon="atualizar"
      tone="destaque"
      size="sm"
    >
      <div className="flex flex-col gap-4">
        <TextArea
          label={warranty ? 'Motivo de Retorno' : 'Por que ele volta'}
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={2000}
          rows={4}
          help={
            warranty
              ? 'O que deu problema na peça. Fica no formulário de Retorno/Garantia.'
              : closure.source === 'sinistro'
                ? 'Vira um comentário do sinistro, e aparece na Atividade.'
                : 'Vira um comentário do acionamento.'
          }
        />
        {error && <Alert tone="erro">{error}</Alert>}
        <ModalActions>
          <Button variant="secundario" disabled={reopen.isPending} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={reason.trim() === ''}
            loading={reopen.isPending}
            onClick={() =>
              reopen.mutate(
                { closure, reason: reason.trim() },
                {
                  /* A linha some da lista; o aviso diz para onde ela foi. */
                  onSuccess: (r) => {
                    onClose();
                    showToast((dismiss) => (
                      <Toast
                        title={
                          warranty
                            ? `A garantia do ${number} foi acionada`
                            : `${number} foi reaberto`
                        }
                        icon="confirmado"
                        onClose={dismiss}
                      >
                        <p className="text-sm">
                          Voltou para {closurePhaseLabel(r.origin, r.phase)}, em{' '}
                          {CLOSURE_ORIGIN_LABEL[r.origin]}.
                        </p>
                      </Toast>
                    ));
                  },
                  onError: (e) =>
                    setError(
                      e instanceof ClosureRefusal && e.code === 'fase_mudou'
                        ? `${e.message} A lista já foi recarregada.`
                        : e.message,
                    ),
                },
              )
            }
          >
            {warranty ? 'Acionar garantia' : 'Reabrir'}
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}
